import {
  Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/system';
import React, { SyntheticEvent } from 'react';

import { UseFormWithErrorsReturn } from '@/hooks/useFormWithErrors';
import { useSpeiBankSearch } from '@/hooks/useSpeiBankSearch';
import { useTranslator } from '@/hooks/useTranslator';
import { type paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { SpeiBank } from '@/types/speiBank';

type OptionProps = React.HTMLAttributes<HTMLLIElement> & { readonly key: string };

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly form: UseFormWithErrorsReturn<PaymentMethodForm>;
}

export const SpeiAccountDetailsFields: React.FC<Props> = (props: Props): React.ReactElement => {
  const { form } = props;
  const tr = useTranslator();

  const { register, setValue, watch } = form;

  const [bankCode, firstName, lastName, recipientType] = watch([
    'bankCode',
    'firstName',
    'lastName',
    'recipientType',
  ]);

  // Search for all banks; it is unlikely that we will have
  // so many to worry about performance.
  const banks = useSpeiBankSearch('');

  React.useEffect((): void => {
    if (recipientType === 'company' && firstName) {
      // For company contacts, use the company name (stored in firstName)
      setValue('receiverName', firstName);
    } else if (recipientType === 'individual' && firstName) {
      // For individual contacts, use firstName + lastName
      const fullName = lastName ? `${firstName} ${lastName}` : firstName;
      setValue('receiverName', fullName);
    }
  }, [firstName, lastName, recipientType, setValue]);

  const renderInput = React.useCallback(
    (props: AutocompleteRenderInputParams): React.ReactElement => (
      <TextField {...props} label={tr('BANK_NAME')} />
    ),
    [tr],
  );

  const handleAutocompleteChange = React.useCallback(
    (_event: SyntheticEvent, value: SpeiBank | null): void => {
      if (value) {
        setValue('bankCode', value.bankCode);
      }
    },
    [setValue],
  );

  const bank = React.useMemo((): SpeiBank | null => {
    if (bankCode === '') {
      return {
        bankCode: '',
        bankName: '',
      };
    }

    return banks.find((bank: SpeiBank): boolean => bank.bankCode === bankCode) ?? null;
  }, [bankCode, banks]);

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.primary">
          {tr('BANK_ACCOUNT_INFORMATION')}
        </Typography>
        <TextField {...register({ name: 'clabe', label: tr('CLABE') })} />
        <Box position="relative">
          <StyledAutocomplete
            value={bank}
            options={banks}
            popupIcon={<></>}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            fullWidth={true}
            onChange={handleAutocompleteChange}
          />
        </Box>
      </Box>
    </>
  );
};

const StyledAutocomplete = styled(Autocomplete<SpeiBank>)`
  .MuiOutlinedInput-root .MuiAutocomplete-input {
    padding: 32px 32px 16px 32px;
  }
  .MuiAutocomplete-inputRoot {
    padding: 0;
  }
`;

const renderOption = (
  { className: _, key, ...props }: OptionProps,
  option: SpeiBank,
  _state: AutocompleteRenderOptionState,
): React.ReactElement => (
  <MenuItem key={key} {...props} value={option.bankCode}>
    {option.bankName}
  </MenuItem>
);

const getOptionLabel = (option: SpeiBank): string => option.bankName;
